import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LOGO_INTRO, SLOGAN } from "@/data/site";

const ease = [0.76, 0, 0.24, 1];

export const Preloader = ({ onDone }) => {
  const [progress, setProgress] = useState(0);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const start = performance.now();
    const dur = 1900;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setProgress(Math.round((1 - Math.pow(1 - p, 3)) * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setTimeout(() => { setLeaving(true); onDone(); document.body.style.overflow = ""; }, 350);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); document.body.style.overflow = ""; };
  }, [onDone]);

  return (
    <motion.div className="fixed inset-0 z-[100] pointer-events-none" data-testid="preloader" aria-hidden={leaving}>
      {[0, 1].map((i) => (
        <motion.div
          key={i}
          initial={{ y: 0 }}
          animate={leaving ? { y: "-100%" } : { y: 0 }}
          transition={{ duration: 1, delay: i * 0.12, ease }}
          className={`absolute inset-x-0 ${i === 0 ? "top-0 h-1/2" : "bottom-0 h-1/2"} grain`}
          style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #2a081a 60%, #600835 100%)", backgroundSize: "100% 200%", backgroundPosition: i === 0 ? "0 0" : "0 100%" }}
        />
      ))}

      <motion.div
        animate={leaving ? { opacity: 0, y: -30, scale: 0.96 } : { opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease }}
        className="absolute inset-0 flex flex-col items-center justify-center px-6"
      >
        <div className="relative">
          <motion.img
            src={LOGO_INTRO}
            alt="Diva Nail Beauty"
            initial={{ opacity: 0, scale: 0.85, filter: "blur(14px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="w-[260px] sm:w-[360px] lg:w-[440px] h-auto select-none"
            draggable="false"
          />
          <motion.div
            initial={{ x: "-120%" }}
            animate={{ x: "220%" }}
            transition={{ duration: 1.4, delay: 0.5, ease: "easeInOut" }}
            className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-20deg] mix-blend-overlay"
          />
        </div>

        <motion.p
          initial={{ opacity: 0, letterSpacing: "0.6em" }}
          animate={{ opacity: 1, letterSpacing: "0.3em" }}
          transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 text-[10px] sm:text-xs uppercase text-pink-100/70 font-medium text-center"
        >
          Esenyurt · İstanbul
        </motion.p>

        <div className="mt-10 w-[220px] sm:w-[300px]">
          <div className="flex justify-between text-[11px] text-pink-100/60 mb-2 font-medium tabular-nums">
            <span>{SLOGAN}</span>
            <span data-testid="preloader-progress">{progress}%</span>
          </div>
          <div className="h-px w-full bg-white/15 overflow-hidden rounded-full">
            <div className="h-full bg-grad-pink" style={{ width: `${progress}%`, transition: "width 80ms linear" }} />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
